import React, { useEffect, useState } from 'react';
import {
  buildEntryFromForm,
  getDefaultFormState,
  validateForm,
} from './runtimeConfigUtils';

const ADD_ACTION_OPTIONS = {
  cors: [
    { value: 'merge', label: 'Add new entry' },
    { value: 'remove', label: 'Remove' },
    { value: 'disable', label: 'Disable' },
  ],
  frame: [
    { value: 'merge', label: 'Add new entry' },
    { value: 'remove', label: 'Remove' },
  ],
  mime: [
    { value: 'merge', label: 'Add new entry' },
    { value: 'remove', label: 'Remove' },
  ],
};

const RuntimeConfigForm = ({
  configId,
  prefill,
  submitting,
  onApply,
  onRequestRemove,
  onClear,
  onClearPrefill,
  formResetKey = 0,
}) => {
  const [action, setAction] = useState('merge');
  const [formState, setFormState] = useState(getDefaultFormState(configId));
  const [editKey, setEditKey] = useState(null);
  const [validationError, setValidationError] = useState('');

  const isEditMode = Boolean(editKey);
  const showEntryFields = isEditMode || action !== 'remove';

  useEffect(() => {
    setAction('merge');
    setFormState(getDefaultFormState(configId));
    setEditKey(null);
    setValidationError('');
  }, [configId]);

  useEffect(() => {
    if (prefill) {
      setAction(prefill.action || 'merge');
      setFormState({ ...getDefaultFormState(configId), ...prefill.formState });
      setEditKey(prefill.isEdit && prefill.editKey ? prefill.editKey : null);
      setValidationError('');
      onClearPrefill();
    }
  }, [prefill, configId, onClearPrefill]);

  const resetToAddMode = () => {
    setAction('merge');
    setFormState(getDefaultFormState(configId));
    setEditKey(null);
    setValidationError('');
  };

  useEffect(() => {
    if (!formResetKey) {
      return;
    }
    resetToAddMode();
    onClear();
  }, [formResetKey, configId, onClear]);

  const handleChange = (field, value) => {
    setFormState((prev) => ({ ...prev, [field]: value }));
    setValidationError('');
  };

  const handleClear = () => {
    resetToAddMode();
    onClear();
  };

  const handleSubmit = async (event) => {
    event.preventDefault();
    const effectiveAction = isEditMode ? 'merge' : (action === 'disable' ? 'merge' : action);
    const normalizedFormState = {
      ...formState,
      ...(configId === 'mime'
        ? { extension: formState.extension?.trim() }
        : { pattern: formState.pattern?.trim() }),
    };
    const error = validateForm(configId, effectiveAction, normalizedFormState);
    if (error) {
      setValidationError(error);
      return;
    }

    let entry = buildEntryFromForm(configId, normalizedFormState);
    if (!isEditMode && action === 'disable' && configId === 'cors') {
      entry = { ...entry, enabled: false };
    }
    if (effectiveAction === 'remove') {
      if (configId === 'mime') {
        entry = { extension: normalizedFormState.extension };
      } else {
        entry = { pattern: normalizedFormState.pattern };
      }
    }

    if (effectiveAction === 'remove') {
      onRequestRemove(entry);
      return;
    }

    const result = await onApply({
      operation: effectiveAction,
      entry,
      ...(isEditMode && editKey ? { previousKey: editKey } : {}),
    });
    if (result?.success) {
      resetToAddMode();
      onClear();
    }
  };

  const actions = ADD_ACTION_OPTIONS[configId] || ADD_ACTION_OPTIONS.frame;
  const busy = submitting;
  const addModeSubmitLabels = {
    merge: { idle: 'Add entry', busy: 'Adding…' },
    remove: { idle: 'Remove entry', busy: 'Removing…' },
    disable: { idle: 'Disable entry', busy: 'Disabling…' },
  };
  const submitLabel = isEditMode
    ? (submitting ? 'Saving…' : 'Save changes')
    : addModeSubmitLabels[action]?.[submitting ? 'busy' : 'idle'] ?? addModeSubmitLabels.merge[submitting ? 'busy' : 'idle'];
  const clearLabel = isEditMode ? 'Cancel edit' : 'Clear';

  return (
    <div
      className={`runtime-config-form-section${isEditMode ? ' runtime-config-form-section--editing' : ''}`}
      data-testid={isEditMode ? 'runtime-config-form-editing' : 'runtime-config-form-add'}
    >
      <h4 className="runtime-config-form-title">
        {isEditMode ? 'Edit entry' : 'Add new entry'}
      </h4>
      {isEditMode && (
        <p className="runtime-config-form-edit-hint" data-testid="runtime-config-edit-hint">
          Editing <strong>{editKey}</strong>. Update any field below and save, or cancel to return to add mode.
        </p>
      )}
      <form className="runtime-config-form" onSubmit={handleSubmit} data-testid="runtime-config-form">
        <div className="runtime-config-form-row runtime-config-form-row-fields">
          {!isEditMode && (
            <div className="runtime-config-field runtime-config-field-action">
              <label htmlFor="runtime-config-action">Action</label>
              <select
                id="runtime-config-action"
                value={action}
                onChange={(e) => setAction(e.target.value)}
                disabled={busy}
              >
                {actions.map((opt) => (
                  <option key={opt.value} value={opt.value}>{opt.label}</option>
                ))}
              </select>
            </div>
          )}

          {configId === 'mime' && (
            <>
              <div className="runtime-config-field">
                <label htmlFor="runtime-config-extension">Extension</label>
                <input
                  id="runtime-config-extension"
                  type="text"
                  value={formState.extension}
                  onChange={(e) => handleChange('extension', e.target.value)}
                  placeholder=".jsonp"
                  disabled={busy}
                />
              </div>
              {showEntryFields && (
                <div className="runtime-config-field runtime-config-field-wide">
                  <label htmlFor="runtime-config-content-type">Content-Type</label>
                  <input
                    id="runtime-config-content-type"
                    type="text"
                    value={formState.contentType}
                    onChange={(e) => handleChange('contentType', e.target.value)}
                    placeholder="application/javascript"
                    disabled={busy}
                  />
                </div>
              )}
            </>
          )}

          {(configId === 'cors' || configId === 'frame') && (
            <div className="runtime-config-field runtime-config-field-wide">
              <label htmlFor="runtime-config-pattern">Pattern</label>
              <input
                id="runtime-config-pattern"
                type="text"
                value={formState.pattern}
                onChange={(e) => handleChange('pattern', e.target.value)}
                placeholder={configId === 'cors'
                  ? 'https://partner.example.com or *.mymathlab.com'
                  : '*.pearson.com'}
                disabled={busy}
              />
            </div>
          )}

          {configId === 'cors' && showEntryFields && (
            <>
              <div className="runtime-config-field">
                <label htmlFor="runtime-config-category">Category (optional)</label>
                <input
                  id="runtime-config-category"
                  type="text"
                  value={formState.category}
                  onChange={(e) => handleChange('category', e.target.value)}
                  placeholder="partner, legacy, ΓÇª"
                  disabled={busy || (!isEditMode && action === 'disable')}
                />
              </div>
              <div className="runtime-config-field runtime-config-field-enabled">
                <label htmlFor="runtime-config-enabled">Enabled</label>
                <select
                  id="runtime-config-enabled"
                  value={formState.enabled === false ? 'false' : 'true'}
                  onChange={(e) => handleChange('enabled', e.target.value === 'true')}
                  disabled={busy || (!isEditMode && action === 'disable')}
                >
                  <option value="true">true</option>
                  <option value="false">false</option>
                </select>
              </div>
            </>
          )}
        </div>

        {validationError && (
          <div className="runtime-config-banner runtime-config-banner-error" role="alert">
            {validationError}
          </div>
        )}

        <div className="runtime-config-form-actions-row">
          <button
            type="submit"
            className="runtime-config-apply-btn"
            disabled={busy}
            data-testid="runtime-config-submit-btn"
          >
            {submitLabel}
          </button>
          <button
            type="button"
            className="runtime-config-secondary-btn"
            onClick={handleClear}
            disabled={busy}
            data-testid="runtime-config-clear-btn"
          >
            {clearLabel}
          </button>
        </div>
      </form>
    </div>
  );
};

export default RuntimeConfigForm;
